import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, ScrollText, ArrowLeft, Sparkles } from "lucide-react";
import api from "../lib/api";
import SEO from "../components/SEO";

const KEYS = [
    { key: "public_disclaimer",   label: "Public responsibility disclaimer" },
    { key: "volunteer_agreement", label: "Volunteer agreement" },
    { key: "contractor_agreement", label: "Contractor agreement" },
    { key: "contractor_insurance", label: "Contractor insurance notice" },
];

const formatLabel = (label) => {
    if (!label) return "—";
    if (label.startsWith("Seed") || label.startsWith("Current")) return label;
    const d = new Date(label);
    if (Number.isNaN(d.getTime())) return label;
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

const InlineSegments = ({ segments }) => (
    <span>
        {segments.map((seg, i) => {
            if (seg.op === "equal") return <span key={i}>{seg.text} </span>;
            if (seg.op === "insert") return <ins key={i} className="bg-green-100 text-green-900 no-underline rounded px-1 mx-0.5">{seg.text} </ins>;
            if (seg.op === "delete") return <del key={i} className="bg-red-100 text-red-900 rounded px-1 mx-0.5">{seg.text} </del>;
            return null;
        })}
    </span>
);

const ParagraphRow = ({ row }) => {
    if (row.kind === "unchanged") {
        return (
            <li className="bg-[#FDFBF7] border border-black/5 rounded-xl p-4 text-sm text-[#4A5568]" data-testid={`diff-para-${row.index}-unchanged`}>
                <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#4A5568] mr-2">¶ {row.index + 1} · unchanged</span>
                {row.after?.text}
            </li>
        );
    }
    if (row.kind === "added") {
        return (
            <li className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-900" data-testid={`diff-para-${row.index}-added`}>
                <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-green-700 mr-2">¶ {row.index + 1} · added</span>
                <ins className="no-underline">{row.after?.text}</ins>
            </li>
        );
    }
    if (row.kind === "removed") {
        return (
            <li className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-900" data-testid={`diff-para-${row.index}-removed`}>
                <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-red-700 mr-2">¶ {row.index + 1} · removed</span>
                <del>{row.before?.text}</del>
            </li>
        );
    }
    // changed
    return (
        <li className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-950 leading-relaxed" data-testid={`diff-para-${row.index}-changed`}>
            <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-amber-800 mr-2">¶ {row.index + 1} · changed</span>
            <InlineSegments segments={row.inline || []} />
        </li>
    );
};

const LegalChanges = () => {
    const [activeKey, setActiveKey] = useState(KEYS[0].key);
    const [history, setHistory] = useState({ revisions: [] });
    const [historyLoading, setHistoryLoading] = useState(true);
    // from_id and to_id are revision IDs from the history collection. Empty = endpoint default.
    const [fromId, setFromId] = useState("");
    const [toId, setToId] = useState("");
    const [diff, setDiff] = useState(null);
    const [diffLoading, setDiffLoading] = useState(true);

    useEffect(() => {
        setHistoryLoading(true);
        api.get(`/legal-docs/${activeKey}/history`)
            .then((r) => setHistory(r.data || { revisions: [] }))
            .catch(() => setHistory({ revisions: [] }))
            .finally(() => setHistoryLoading(false));
        // Reset selection on tab change
        setFromId("");
        setToId("");
    }, [activeKey]);

    useEffect(() => {
        setDiffLoading(true);
        const params = {};
        if (fromId) params.from_id = fromId;
        if (toId) params.to_id = toId;
        api.get(`/legal-docs/${activeKey}/diff`, { params })
            .then((r) => setDiff(r.data || null))
            .catch(() => setDiff(null))
            .finally(() => setDiffLoading(false));
    }, [activeKey, fromId, toId]);

    return (
        <div className="bg-[#FDFBF7] min-h-[calc(100vh-160px)]" data-testid="legal-changes-page">
            <SEO
                title="Legal document changes · Public revision history"
                description="Every edit to our hold-harmless and contractor agreements is published here with a side-by-side redline. Independent transparency you can verify."
                path="/legal/changes"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "WebPage",
                    name: "Reddog Community Foundation — Legal document changes",
                    description: "Public revision timeline + side-by-side redline for each legal document.",
                }}
            />

            <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-12 lg:py-16">
                <Link to="/about#governance" className="inline-flex items-center gap-1.5 text-xs text-[#4A5568] hover:text-[#1C252E] mb-6" data-testid="legal-changes-back-link">
                    <ArrowLeft className="w-3 h-3" strokeWidth={2} /> Back to Governance
                </Link>

                <div className="mb-10">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#F2D8D5] rounded-full mb-4">
                        <Sparkles className="w-3 h-3 text-[#E53935]" strokeWidth={2} />
                        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#E53935]">Public transparency</span>
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl text-[#1C252E] font-medium tracking-tight">
                        Legal document changes
                    </h1>
                    <p className="text-base text-[#4A5568] mt-4 max-w-2xl leading-relaxed">
                        Every edit to our hold-harmless and contractor copy is logged below with the editor's name, the timestamp, and a side-by-side <strong>redline diff</strong>. Compare any two revisions — or check what changed since our original seed text.
                    </p>
                </div>

                {/* Tab pills */}
                <div className="flex flex-wrap gap-2 mb-8" role="tablist" aria-label="Choose a legal document">
                    {KEYS.map((k) => (
                        <button
                            key={k.key}
                            role="tab"
                            aria-selected={activeKey === k.key}
                            onClick={() => setActiveKey(k.key)}
                            data-testid={`legal-changes-tab-${k.key}`}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition border ${activeKey === k.key ? "bg-[#E53935] text-white border-[#E53935]" : "bg-white text-[#1C252E] border-black/10 hover:bg-[#F4F1EA]"}`}
                        >
                            <FileText className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5" strokeWidth={1.5} />
                            {k.label}
                        </button>
                    ))}
                </div>

                <div className="grid lg:grid-cols-[280px_1fr] gap-8">
                    {/* Revision timeline */}
                    <aside className="bg-white border border-black/5 rounded-2xl p-5 h-fit" data-testid="legal-changes-timeline">
                        <h2 className="font-heading text-lg text-[#1C252E] flex items-center gap-2 mb-4">
                            <ScrollText className="w-4 h-4 text-[#E53935]" strokeWidth={1.5} />
                            Revision history
                        </h2>
                        {historyLoading ? (
                            <p className="text-sm text-[#4A5568] italic">Loading…</p>
                        ) : history.revisions.length === 0 ? (
                            <p className="text-sm text-[#4A5568]" data-testid="legal-changes-no-history">
                                No edits yet. The original seed text is in effect.
                            </p>
                        ) : (
                            <ol className="space-y-2">
                                <li>
                                    <button
                                        onClick={() => { setFromId(""); setToId(""); }}
                                        data-testid="legal-changes-rev-seed"
                                        className={`w-full text-left p-3 rounded-xl border text-xs transition ${!fromId && !toId ? "bg-[#F4F1EA] border-[#E53935]/30" : "bg-white border-black/5 hover:bg-[#FDFBF7]"}`}
                                    >
                                        <div className="font-bold text-[#1C252E]">Seed → Current</div>
                                        <div className="text-[#4A5568] mt-0.5">Default: shows everything that ever changed.</div>
                                    </button>
                                </li>
                                {history.revisions.map((r) => {
                                    const selected = fromId === r.id || toId === r.id;
                                    return (
                                        <li key={r.id}>
                                            <button
                                                onClick={() => {
                                                    // Cycle: click sets `to`, click again sets `from`, click again clears.
                                                    if (toId === r.id) { setToId(""); }
                                                    else if (fromId === r.id) { setFromId(""); }
                                                    else if (!toId) { setToId(r.id); }
                                                    else if (!fromId) { setFromId(r.id); }
                                                    else { setFromId(r.id); setToId(""); }
                                                }}
                                                data-testid={`legal-changes-rev-${r.id}`}
                                                className={`w-full text-left p-3 rounded-xl border text-xs transition ${selected ? "bg-[#F4F1EA] border-[#E53935]/40" : "bg-white border-black/5 hover:bg-[#FDFBF7]"}`}
                                            >
                                                <div className="font-bold text-[#1C252E] flex items-center justify-between">
                                                    <span>{formatLabel(r.changed_at)}</span>
                                                    {fromId === r.id && <span className="text-[9px] uppercase font-bold text-[#E53935] bg-[#F2D8D5] px-1.5 py-0.5 rounded-full">From</span>}
                                                    {toId === r.id && <span className="text-[9px] uppercase font-bold text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full">To</span>}
                                                </div>
                                                <div className="text-[#4A5568] mt-0.5 capitalize">{r.action} · {r.changed_by}</div>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ol>
                        )}
                    </aside>

                    {/* Diff viewer */}
                    <article className="bg-white border border-black/5 rounded-2xl p-6 lg:p-8" data-testid="legal-changes-diff">
                        {diffLoading || !diff ? (
                            <p className="text-sm text-[#4A5568] italic">Loading diff…</p>
                        ) : (
                            <>
                                <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2 pb-4 border-b border-black/5">
                                    <div>
                                        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">From</p>
                                        <p className="text-sm font-medium text-[#1C252E]" data-testid="diff-from-label">{formatLabel(diff.before.meta.label)} · {diff.before.meta.by}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">To</p>
                                        <p className="text-sm font-medium text-[#1C252E]" data-testid="diff-to-label">{formatLabel(diff.after.meta.label)} · {diff.after.meta.by}</p>
                                    </div>
                                </div>

                                <div className="flex flex-wrap gap-2 my-5 text-[11px] uppercase tracking-[0.12em] font-bold" data-testid="diff-summary">
                                    {diff.summary.title_changed && <span className="bg-amber-100 text-amber-900 px-2 py-1 rounded-full">title changed</span>}
                                    {diff.summary.added > 0 && <span className="bg-green-100 text-green-900 px-2 py-1 rounded-full">{diff.summary.added} added</span>}
                                    {diff.summary.removed > 0 && <span className="bg-red-100 text-red-900 px-2 py-1 rounded-full">{diff.summary.removed} removed</span>}
                                    {diff.summary.changed > 0 && <span className="bg-amber-100 text-amber-900 px-2 py-1 rounded-full">{diff.summary.changed} edited</span>}
                                    {diff.summary.unchanged > 0 && <span className="bg-[#F4F1EA] text-[#4A5568] px-2 py-1 rounded-full">{diff.summary.unchanged} unchanged</span>}
                                    {diff.summary.added + diff.summary.removed + diff.summary.changed === 0 && !diff.summary.title_changed && <span className="bg-[#F4F1EA] text-[#4A5568] px-2 py-1 rounded-full">No changes</span>}
                                </div>

                                <div className="mb-6">
                                    <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568] mb-2">Title</p>
                                    {diff.summary.title_changed ? (
                                        <p className="text-base text-[#1C252E] leading-relaxed" data-testid="diff-title-inline">
                                            <InlineSegments segments={diff.title_inline} />
                                        </p>
                                    ) : (
                                        <p className="text-base text-[#4A5568] italic">{diff.after.title}</p>
                                    )}
                                </div>

                                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568] mb-2">Paragraphs</p>
                                <ol className="space-y-3">
                                    {diff.paragraphs.map((row) => (
                                        <ParagraphRow key={row.index} row={row} />
                                    ))}
                                </ol>
                            </>
                        )}
                    </article>
                </div>
            </section>
        </div>
    );
};

export default LegalChanges;
